'use client';

import { useState, useEffect } from 'react';
import { AISparkle } from '@/components/shared/AIComponents';
import Image from 'next/image';

const analysisSteps = [
  { label: 'Scanning document...', progress: 20 },
  { label: 'Extracting text...', progress: 40 },
  { label: 'Identifying sections...', progress: 60 },
  { label: 'Analyzing risks...', progress: 80 },
  { label: 'Complete!', progress: 100 },
];

const extractedData = {
  noticeType: 'Section 143(1) - Intimation',
  assessmentYear: '2023-24',
  issueDate: '15 Dec 2024',
  dueDate: '15 Jan 2025',
  demandAmount: '₹2,45,000',
  riskLevel: 'Medium',
  keyFindings: [
    'Mismatch in TDS claimed vs Form 26AS',
    'Interest income not declared',
    'Penalty u/s 234A applicable',
  ],
};

export default function DocumentAnalysisShowcase() {
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [showResults, setShowResults] = useState(false);

  const startAnalysis = () => {
    setIsAnalyzing(true);
    setCurrentStep(0);
    setShowResults(false);

    // Animate through steps
    let step = 0;
    const interval = setInterval(() => {
      step++;
      setCurrentStep(step);
      if (step >= analysisSteps.length - 1) {
        clearInterval(interval);
        setTimeout(() => {
          setIsAnalyzing(false);
          setShowResults(true);
        }, 500);
      }
    }, 800);
  };

  useEffect(() => {
    // Auto-start demo after component mounts
    const timer = setTimeout(startAnalysis, 1000);
    return () => clearTimeout(timer);
  }, []);

  return (
    <section className="py-12 lg:py-24 bg-gradient-to-br from-slate-50 to-white overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-center">
          {/* Left - Content */}
          <div className="text-center lg:text-left">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 lg:px-4 lg:py-2 bg-cyan-100 rounded-full mb-4 lg:mb-6">
              <svg className="w-4 h-4 text-cyan-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <span className="text-xs lg:text-sm font-semibold text-cyan-700">Document Analysis</span>
            </div>

            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 mb-4 lg:mb-6 px-4 lg:px-0">
              Upload Once, <br />
              <span className="bg-gradient-to-r from-cyan-600 to-blue-600 bg-clip-text text-transparent">
                Understand Everything
              </span>
            </h2>

            <p className="text-sm lg:text-lg text-gray-600 mb-6 lg:mb-8 px-4 lg:px-0">
              Finkr&apos;s AI scans tax notices in seconds, extracting critical information like
              deadlines, demand amounts, and risk factors. No more manual reading of
              complex legal documents.
            </p>

            <div className="space-y-3 lg:space-y-4 mb-6 lg:mb-8 text-left max-w-sm mx-auto lg:mx-0">
              {[
                { icon: '🔍', text: 'Auto-detects notice type and sections' },
                { icon: '⚠️', text: 'Identifies risks and required actions' },
                { icon: '📅', text: 'Extracts deadlines and due dates' },
                { icon: '💰', text: 'Calculates demand amounts automatically' },
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-2 lg:gap-3">
                  <span className="text-lg lg:text-xl">{item.icon}</span>
                  <span className="text-sm lg:text-base text-gray-700">{item.text}</span>
                </div>
              ))}
            </div>

            <button
              onClick={startAnalysis}
              disabled={isAnalyzing}
              className="w-full sm:w-auto h-12 lg:h-14 px-6 lg:px-8 bg-gradient-to-r from-cyan-600 to-blue-600 text-white font-bold rounded-xl shadow-lg shadow-cyan-500/30 hover:shadow-xl hover:shadow-cyan-500/40 hover:scale-105 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 touch-manipulation"
            >
              <AISparkle className="w-4 h-4 lg:w-5 lg:h-5" />
              <span className="text-sm lg:text-base">{isAnalyzing ? 'Analyzing...' : 'Try Demo Analysis'}</span>
            </button>
          </div>

          {/* Right - Demo Visual */}
          <div className="relative order-first lg:order-last">
            {/* Document Card */}
            <div className="bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden">
              {/* Header */}
              <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-4 py-3 lg:px-6 lg:py-4 border-b border-gray-100">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 lg:gap-3">
                    <div className="w-8 h-8 lg:w-10 lg:h-10 bg-red-100 rounded-lg flex items-center justify-center">
                      <svg className="w-5 h-5 lg:w-6 lg:h-6 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-sm lg:text-base font-semibold text-gray-800">Notice_143_ABC_Corp.pdf</p>
                      <p className="text-[10px] lg:text-xs text-gray-500">2.4 MB</p>
                    </div>
                  </div>
                  {showResults && (
                    <div className="px-2 py-0.5 lg:px-3 lg:py-1 bg-green-100 text-green-700 text-xs lg:text-sm font-medium rounded-full flex items-center gap-1">
                      <svg className="w-3 h-3 lg:w-4 lg:h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      Analyzed
                    </div>
                  )}
                </div>
              </div>

              {/* Content */}
              <div className="p-4 lg:p-6">
                {/* Progress */}
                {isAnalyzing && (
                  <div className="mb-4 lg:mb-6">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 lg:w-5 lg:h-5 rounded-full bg-gradient-to-r from-[#0D60E6] to-cyan-500 animate-spin" style={{ animationDuration: '2s' }} />
                        <span className="text-xs lg:text-sm font-medium text-gray-700">
                          {analysisSteps[currentStep]?.label}
                        </span>
                      </div>
                      <span className="text-xs lg:text-sm text-gray-500">
                        {analysisSteps[currentStep]?.progress}%
                      </span>
                    </div>
                    <div className="h-1.5 lg:h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-[#0D60E6] to-cyan-500 transition-all duration-500 rounded-full"
                        style={{ width: `${analysisSteps[currentStep]?.progress || 0}%` }}
                      />
                    </div>
                  </div>
                )}

                {/* Results */}
                {showResults && (
                  <div className="space-y-3 lg:space-y-4 animate-fadeIn">
                    {/* Key Info Grid */}
                    <div className="grid grid-cols-2 gap-2 lg:gap-4">
                      <div className="p-3 lg:p-4 bg-gray-50 rounded-xl">
                        <p className="text-[10px] lg:text-xs text-gray-500 mb-0.5 lg:mb-1">Notice Type</p>
                        <p className="font-semibold text-gray-800 text-xs lg:text-sm">{extractedData.noticeType}</p>
                      </div>
                      <div className="p-3 lg:p-4 bg-gray-50 rounded-xl">
                        <p className="text-[10px] lg:text-xs text-gray-500 mb-0.5 lg:mb-1">Assessment Year</p>
                        <p className="font-semibold text-gray-800 text-xs lg:text-sm">{extractedData.assessmentYear}</p>
                      </div>
                      <div className="p-3 lg:p-4 bg-red-50 rounded-xl border border-red-100">
                        <p className="text-[10px] lg:text-xs text-gray-500 mb-0.5 lg:mb-1">Due Date</p>
                        <p className="font-semibold text-red-600 text-xs lg:text-sm">{extractedData.dueDate}</p>
                      </div>
                      <div className="p-3 lg:p-4 bg-orange-50 rounded-xl border border-orange-100">
                        <p className="text-[10px] lg:text-xs text-gray-500 mb-0.5 lg:mb-1">Demand Amount</p>
                        <p className="font-semibold text-orange-600 text-xs lg:text-sm">{extractedData.demandAmount}</p>
                      </div>
                    </div>

                    {/* Risk Badge */}
                    <div className="flex items-center gap-2 p-2.5 lg:p-3 bg-yellow-50 border border-yellow-200 rounded-xl">
                      <span className="text-base lg:text-lg">⚠️</span>
                      <div>
                        <p className="text-xs lg:text-sm font-medium text-yellow-800">Risk Level: {extractedData.riskLevel}</p>
                        <p className="text-[10px] lg:text-xs text-yellow-600">Response recommended within 7 days</p>
                      </div>
                    </div>

                    {/* Key Findings */}
                    <div>
                      <p className="text-xs lg:text-sm font-semibold text-gray-700 mb-1.5 lg:mb-2">Key Findings:</p>
                      <div className="space-y-1.5 lg:space-y-2">
                        {extractedData.keyFindings.map((finding, i) => (
                          <div key={i} className="flex items-start gap-2 text-xs lg:text-sm text-gray-600">
                            <span className="text-[#0D60E6] mt-0.5">•</span>
                            <span>{finding}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex flex-col sm:flex-row gap-2 lg:gap-3 pt-1 lg:pt-2">
                      <button className="flex-1 h-9 lg:h-10 bg-gradient-to-r from-[#0D60E6] to-[#0D52C2] text-white text-xs lg:text-sm font-semibold rounded-lg flex items-center justify-center gap-2 touch-manipulation">
                        <AISparkle className="w-3.5 h-3.5 lg:w-4 lg:h-4" />
                        Generate Response
                      </button>
                      <button className="h-9 lg:h-10 px-4 border border-gray-200 text-gray-600 text-xs lg:text-sm font-semibold rounded-lg hover:bg-gray-50 transition touch-manipulation">
                        View Details
                      </button>
                    </div>
                  </div>
                )}

                {/* Placeholder when not analyzing */}
                {!isAnalyzing && !showResults && (
                  <div className="text-center py-6 lg:py-8">
                    <div className="w-12 h-12 lg:w-16 lg:h-16 mx-auto bg-gray-100 rounded-xl flex items-center justify-center mb-3 lg:mb-4">
                      <svg className="w-6 h-6 lg:w-8 lg:h-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                      </svg>
                    </div>
                    <p className="text-xs lg:text-sm text-gray-500">Click &quot;Try Demo Analysis&quot; to see AI in action</p>
                  </div>
                )}
              </div>
            </div>

            {/* Decorative elements - hidden on mobile */}
            <div className="hidden lg:block absolute -top-4 -right-4 w-24 h-24 bg-cyan-200/30 rounded-full blur-2xl"></div>
            <div className="hidden lg:block absolute -bottom-4 -left-4 w-32 h-32 bg-blue-200/30 rounded-full blur-2xl"></div>
          </div>
        </div>
      </div>
    </section>
  );
}
